import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Grid, Card, CardContent, Box, Button, Chip } from '@mui/material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import FactoryIcon from '@mui/icons-material/Factory';
import GavelIcon from '@mui/icons-material/Gavel';
import CampaignIcon from '@mui/icons-material/Campaign';

export default function Industries() {
  const items = [
    { id: 'healthcare', title: 'Healthcare', icon: <MedicalServicesIcon fontSize="large" />, desc: 'AI-driven diagnostics, patient insights and clinical decision support to improve outcomes.' },
    { id: 'finance', title: 'Finance', icon: <AccountBalanceIcon fontSize="large" />, desc: 'Risk modelling, fraud detection and intelligent automation for finance teams.' },
    { id: 'retail', title: 'Retail', icon: <StorefrontIcon fontSize="large" />, desc: 'Customer analytics, personalization and real-time recommendations to grow revenue.' },
    { id: 'manufacturing', title: 'Manufacturing', icon: <FactoryIcon fontSize="large" />, desc: 'Predictive maintenance, quality inspection and process optimisation at scale.' },
    { id: 'local-bar-associations', title: 'Local Bar Associations', icon: <GavelIcon fontSize="large" />, desc: 'Tools for legal community engagement, knowledge management and compliance.' },
    { id: 'digital-marketing', title: 'Digital Marketing', icon: <CampaignIcon fontSize="large" />, desc: 'Performance marketing, analytics and attribution to maximise ROI.' }
  ];

  const navigate = useNavigate();

  React.useEffect(() => {
    // Smooth-scroll to anchor if present in URL on mount
    if (typeof window !== 'undefined' && window.location && window.location.hash) {
      const id = window.location.hash.replace('#', '');
      if (id) {
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            // compute offset for any fixed header(s)
            const fixedEls = Array.from(document.querySelectorAll('body *')).filter((n) => {
              try {
                const s = window.getComputedStyle(n);
                return (s.position === 'fixed' || s.position === '-webkit-sticky') && parseFloat(s.top || '0') >= 0;
              } catch (e) {
                return false;
              }
            });
            const offset = fixedEls.reduce((sum, n) => sum + (n.getBoundingClientRect().height || 0), 0);
            const top = el.getBoundingClientRect().top + window.pageYOffset - offset - 12;
            window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
          }
        }, 60);
      }
    }
  }, []);

  return (
    <Container sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', maxWidth: 920, mx: 'auto', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>Industries We Are Transforming</Typography>
        <Typography variant="subtitle1" color="text.secondary">We partner with organizations across sectors to deliver AI, cloud and software solutions that solve real business problems and drive measurable impact.</Typography>
      </Box>

      <Grid container spacing={3}>
        {items.map((it) => (
          <Grid item xs={12} sm={6} md={4} key={it.id} id={it.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                borderRadius: 2,
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: (theme) => `0 8px 30px ${theme.palette.mode === 'light' ? 'rgba(16,24,40,0.08)' : 'rgba(0,0,0,0.6)'}`
                }
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (theme) => `${theme.palette.primary.main}22` /* subtle tint */
                  }} aria-hidden>
                    {it.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{it.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{it.desc}</Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label="AI & Cloud" size="small" color="primary" />
                  <Button size="small" sx={{ textTransform: 'none' }} onClick={() => navigate(`/industries/${it.id}`)}>Learn more</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Button variant="contained" color="primary" href="/services" sx={{ px: 4, py: 1.5, fontWeight: 700 }}>Explore Our Solutions</Button>
      </Box>
    </Container>
  );
}
